<template>
  <div class="custom-pagination" v-if="total > 0">
    <div class="pagination-container">
      <!-- 上一页 -->
      <div 
        class="page-item prev-btn" 
        :class="{ disabled: currentPage === 1 }"
        @click="handlePageChange(currentPage - 1)"
      >
        <v-icon icon="mdi-chevron-left" size="16" class="mr-1"></v-icon>
        <span>上一页</span>
      </div>

      <div class="divider"></div>

      <!-- 页码列表 -->
      <template v-for="(page, index) in pageList" :key="index">
        <div 
          v-if="page !== '...'"
          class="page-item page-num" 
          :class="{ active: currentPage === page }"
          @click="handlePageChange(page)"
        >
          {{ page }}
        </div>
        <div v-else class="page-item ellipsis">
          ...
        </div>
        
        <!-- 分隔线，除了最后一个元素外都显示 -->
        <div v-if="index < pageList.length - 1" class="divider"></div>
      </template>

      <div class="divider"></div>

      <!-- 下一页 -->
      <div 
        class="page-item next-btn" 
        :class="{ disabled: currentPage === totalPages }"
        @click="handlePageChange(currentPage + 1)"
      >
        <span>下一页</span>
        <v-icon icon="mdi-chevron-right" size="16" class="ml-1"></v-icon>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: Number,
    default: 1
  },
  total: {
    type: Number,
    required: true
  },
  pageSize: {
    type: Number,
    default: 10
  }
})

const emit = defineEmits(['update:modelValue', 'change'])

const currentPage = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const totalPages = computed(() => {
  return Math.ceil(props.total / props.pageSize) || 1
})

// 计算需要显示的页码列表
const pageList = computed(() => {
  const current = currentPage.value
  const total = totalPages.value
  const list = []

  if (total <= 7) {
    // 总页数少于等于7页，全部显示
    for (let i = 1; i <= total; i++) {
      list.push(i)
    }
  } else {
    // 总页数大于7页，需要显示省略号
    if (current <= 4) {
      // 当前页在前4页
      for (let i = 1; i <= 5; i++) {
        list.push(i)
      }
      list.push('...')
      list.push(total)
    } else if (current >= total - 3) {
      // 当前页在最后4页
      list.push(1)
      list.push('...')
      for (let i = total - 4; i <= total; i++) {
        list.push(i)
      }
    } else {
      // 当前页在中间
      list.push(1)
      list.push('...')
      list.push(current - 1)
      list.push(current)
      list.push(current + 1)
      list.push('...')
      list.push(total)
    }
  }
  return list
})

const handlePageChange = (page) => {
  if (page === '...' || page < 1 || page > totalPages.value || page === currentPage.value) {
    return
  }
  currentPage.value = page
  emit('change', page)
}
</script>

<style lang="scss" scoped>
.custom-pagination {
  display: flex;
  justify-content: flex-end;
  
  .pagination-container {
    display: flex;
    align-items: center;
    height: 32px;
    border: 1px solid #E5E6EB;
    border-radius: 16px;
    background: #FFFFFF;
    overflow: hidden;
  }

  .page-item {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-width: 32px;
    padding: 0 12px;
    font-size: 14px;
    color: #4E5969;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;
    background: transparent;

    &:hover:not(.disabled):not(.ellipsis):not(.active) {
      color: #1D2129;
      background: #F2F3F5;
    }

    &.active {
      color: #3A68FF;
      font-weight: 500;
    }

    &.disabled {
      color: #C9CDD4;
      cursor: not-allowed;
    }

    &.ellipsis {
      cursor: default;
      padding: 0 8px;
    }

    &.page-num {
      padding: 0 8px;
    }
  }

  .divider {
    width: 1px;
    height: 100%;
    background-color: #E5E6EB;
  }
}
</style>
